import { useState, useRef, useEffect } from 'react';
import { OUTPUT_TYPES, MODELS } from '../constants';
import { useNavigate } from 'react-router-dom';
import { useAuth, FREE_LIMIT } from '../context/AuthContext';
import { analytics } from '../lib/analytics';
import { useTheme } from '../context/ThemeContext';
import { insertEntry, uploadFile } from '../lib/supabase';

const INITIAL = {
  title: '', type: 'image', model: '', modelCustom: '',
  params: '', prompt: '', textOutput: '', tags: '', notes: '',
  sourceUrl: '',
  isPublic: true,
  folderId: '',
};

function FormModelDropdown({ value, onChange, hasError }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const btnRef = useRef(null);
  const { isDark } = useTheme();

  const surface  = isDark ? '#1a1a1c' : '#ffffff';
  const surface2 = isDark ? '#232326' : '#f0eeea';
  const bg       = isDark ? '#0e0e0f' : '#f5f4f0';

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allGroups = Object.entries(MODELS);
  const filtered = search.trim()
    ? allGroups
        .map(([g, ms]) => [g, ms.filter(m => m.toLowerCase().includes(search.toLowerCase()))])
        .filter(([, ms]) => ms.length)
    : allGroups;

  // Calculate dropdown position
  const getDropdownStyle = () => {
    if (!btnRef.current) return {};
    const rect = btnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropHeight = 260;

    if (spaceBelow >= dropHeight || spaceBelow >= spaceAbove) {
      return { top: rect.bottom + 4, left: rect.left, width: rect.width };
    } else {
      return { bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width };
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          background: bg,
          border: `0.5px solid ${hasError ? 'rgba(226,75,74,0.6)' : 'var(--border2)'}`,
          borderRadius: 'var(--radius-sm)',
          padding: '9px 12px',
          color: value ? 'var(--text)' : 'var(--dim)',
          fontFamily: 'var(--font)',
          fontSize: 13,
          outline: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || 'Select a model...'}
        </span>
        <span style={{ fontSize: 9, flexShrink: 0, marginLeft: 8 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'fixed',
          ...getDropdownStyle(),
          background: surface,
          border: '0.5px solid var(--border2)',
          borderRadius: 'var(--radius)',
          zIndex: 99999,
          maxHeight: 260,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
        }}>
          {/* Search */}
          <div style={{ padding: '8px 10px', borderBottom: '0.5px solid var(--border)', background: surface, flexShrink: 0 }}>
            <input
              autoFocus
              placeholder="Search models..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%',
                background: bg,
                border: '0.5px solid var(--border2)',
                borderRadius: 'var(--radius-sm)',
                padding: '6px 10px',
                color: 'var(--text)',
                fontFamily: 'var(--font)',
                fontSize: 12,
                outline: 'none',
              }}
            />
          </div>

          {/* Options */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.map(([group, models]) => (
              <div key={group}>
                <div style={{
                  padding: '6px 12px 3px',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  color: 'var(--dim)',
                  background: surface2,
                  borderBottom: '0.5px solid var(--border)',
                  position: 'sticky',
                  top: 0,
                }}>
                  {group}
                </div>
                {models.map(m => (
                  <div
                    key={m}
                    onClick={() => { onChange(m); setOpen(false); setSearch(''); }}
                    style={{
                      padding: '8px 14px',
                      fontSize: 13,
                      color: value === m ? 'var(--accent)' : 'var(--text)',
                      background: value === m ? 'var(--accent-dim)' : surface,
                      cursor: 'pointer',
                      borderBottom: '0.5px solid var(--border)',
                    }}
                    onMouseEnter={e => { if (value !== m) e.currentTarget.style.background = surface2; }}
                    onMouseLeave={e => { if (value !== m) e.currentTarget.style.background = surface; }}
                  >
                    {m}
                  </div>
                ))}
              </div>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: '16px', fontSize: 12.5, color: 'var(--dim)', textAlign: 'center' }}>
                No models match "{search}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function EntryForm({ onSave, onClose, folders, prefill, isOnboarding }) {
  const { user, isPro, isAdmin, canAddEntry, remainingFree, entryCount, profile } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(prefill ? { ...INITIAL, ...prefill } : INITIAL);
  const [errors, setErrors] = useState({});
  const [file, setFile]           = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null); // object URL for instant preview
  const [saving, setSaving]       = useState(false);
  const [uploadPhase, setUploadPhase] = useState(''); // 'compressing' | 'uploading' | 'saving' | ''
  const [uploadPct, setUploadPct] = useState(0);
  const [dupeEntry, setDupeEntry] = useState(null);  // possible duplicate found before save
  const [dupeDismissed, setDupeDismissed] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    // Reset dupe state when user is actively editing
    if (k === 'title' || k === 'prompt') {
      setDupeEntry(null);
      setDupeDismissed(false);
    }
  };

  // Check for duplicate entries by title or prompt similarity
  const checkDupe = async (title, prompt) => {
    if (!user || isOnboarding || dupeDismissed) return;
    const trimTitle  = title.trim().toLowerCase();
    const trimPrompt = prompt.trim().toLowerCase().slice(0, 120);
    if (!trimTitle && !trimPrompt) return;
    try {
      const { supabase } = await import('../lib/supabase');
      // Look for entries with very similar title (exact match after normalising)
      const { data } = await supabase
        .from('entries')
        .select('id, title, created_at')
        .eq('user_id', user.id)
        .ilike('title', trimTitle)
        .limit(1);
      if (data?.length) setDupeEntry(data[0]);
    } catch { /* non-fatal */ }
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = true;
    if (!form.prompt.trim()) e.prompt = true;
    if (!(form.modelCustom.trim() || form.model)) e.model = true;
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleFile = e => {
    const f = e.target.files[0];
    if (!f) return;
    // Revoke previous object URL to free memory
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    if (f.type.startsWith('image/') || f.type.startsWith('video/')) {
      const url = URL.createObjectURL(f); // instant — no encoding needed
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setUploadPct(0);
    try {
      let fileUrl = null;
      let fileName = null;

      if (file) {
        const uploaded = await uploadFile(file, user.id, ({ phase, percent }) => {
          setUploadPhase(phase);
          setUploadPct(percent);
        });
        fileUrl = uploaded.url;
        fileName = uploaded.name;
      }

      setUploadPhase('saving');
      const model = form.modelCustom.trim() || form.model;
      const tags = form.tags.split(',').map(t => t.trim()).filter(Boolean);

      // ── Onboarding mode: fake entry, no DB write ──────────
      if (isOnboarding) {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        const fakeEntry = {
          id:         `onboarding-${Date.now()}`,
          title:      form.title.trim(),
          type:       form.type,
          model,
          prompt:     form.prompt.trim(),
          tags,
          is_public:  form.isPublic,
          created_at: new Date().toISOString(),
          _onboarding: true,
        };
        onSave(fakeEntry);
        return;
      }

      // ── Normal mode: save to Supabase ─────────────────────
      const entry = await insertEntry({
        title: form.title.trim(),
        type: form.type,
        model,
        params: form.params.trim() || null,
        prompt: form.prompt.trim(),
        text_output: form.textOutput.trim() || null,
        notes: form.notes.trim() || null,
        source_url: form.sourceUrl.trim() || null,
        tags,
        file_url: fileUrl,
        file_name: fileName,
        is_public: form.isPublic,
        folder_id: form.folderId || null,
        user_id: user.id,
      });

      // Revoke object URL now we're done
      if (previewUrl) URL.revokeObjectURL(previewUrl);

      // Fire upgrade nudge email at 15 saves (once only, fire-and-forget)
      const newCount = (entryCount || 0) + 1;
      if (newCount === 15 && user?.email && profile?.plan === 'free') {
        fetch('/api/emails', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'upgrade-nudge',
            email: user.email,
            username: profile?.username,
            saveCount: newCount,
            limit: FREE_LIMIT,
          }),
        }).catch(() => {});
      }

      analytics.entrySaved({ type: form.type, isPublic: form.isPublic });
      onSave(entry);
    } catch (e) {
      // Postgres unique violation (23505) = duplicate title for this user
      if (e.code === '23505' || e.message?.includes('entries_user_title_unique')) {
        setDupeEntry({ title: form.title.trim(), created_at: new Date().toISOString() });
        setDupeDismissed(false);
        // Scroll to the warning
        document.querySelector('.btn-save')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        alert('Error saving: ' + e.message);
      }
    }
    setSaving(false);
    setUploadPhase('');
    setUploadPct(0);
  };

  const inputStyle = k => ({
    borderColor: errors[k] ? 'rgba(226,75,74,0.6)' : undefined,
  });

  return (
    <div className="right-panel" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="panel-header">
        <span className="panel-title">New entry</span>
        <button className="panel-close" onClick={onClose}>×</button>
      </div>

      <div className="form-body" style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {/* Limit wall */}
        {!canAddEntry && (
          <div style={{
            background: 'rgba(226,75,74,0.08)', border: '0.5px solid rgba(226,75,74,0.3)',
            borderRadius: 'var(--radius)', padding: '16px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>⚡</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>Free limit reached</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.6 }}>
              You've used all 10 free saves. Upgrade to Pro for unlimited entries and private prompts.
            </div>
            <button onClick={() => { onClose(); navigate('/premium'); }} style={{
              background: 'var(--accent)', color: '#fff', border: 'none',
              borderRadius: 'var(--radius-sm)', padding: '9px 20px',
              fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
            }}>Upgrade to Pro — $9/mo</button>
          </div>
        )}

        {/* Nudge */}
        {canAddEntry && !isPro && !isAdmin && remainingFree <= 3 && (
          <div style={{
            background: 'var(--accent-dim)', border: '0.5px solid var(--accent-border)',
            borderRadius: 'var(--radius-sm)', padding: '10px 14px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          }}>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
              {remainingFree} free save{remainingFree !== 1 ? 's' : ''} remaining
            </span>
            <button onClick={() => { onClose(); navigate('/premium'); }} style={{
              background: 'none', border: '0.5px solid var(--accent-border)',
              borderRadius: 4, padding: '3px 10px', fontFamily: 'var(--font)',
              fontSize: 11, color: 'var(--accent)', cursor: 'pointer',
            }}>Upgrade</button>
          </div>
        )}

        <div className="form-field">
          <label className="form-label">Title *</label>
          <input className="form-input" placeholder="Short descriptive name"
            value={form.title} onChange={e => set('title', e.target.value)}
            onBlur={() => checkDupe(form.title, form.prompt)}
            style={inputStyle('title')} />
        </div>

        <div className="form-field">
          <label className="form-label">Output type</label>
          <div className="type-grid">
            {OUTPUT_TYPES.map(t => (
              <div key={t.id} className={`type-opt ${form.type === t.id ? 'selected' : ''}`}
                onClick={() => set('type', t.id)}>
                <span style={{ fontSize: 14 }}>{t.icon}</span> {t.label}
              </div>
            ))}
          </div>
        </div>

        <div className="form-field">
          <label className="form-label">Prompt used *</label>
          <textarea className="form-textarea" placeholder="Paste the full prompt you used..."
            value={form.prompt} onChange={e => set('prompt', e.target.value)}
            style={{ ...inputStyle('prompt'), minHeight: 100 }} />
        </div>

        <div className="form-field">
          <label className="form-label">LLM / Tool *</label>
          <FormModelDropdown
            value={form.model}
            onChange={v => set('model', v)}
            hasError={errors.model}
          />
        </div>

        <div className="form-field">
          <label className="form-label">Custom model / version</label>
          <input className="form-input" placeholder="e.g. Midjourney v7 (overrides dropdown)"
            value={form.modelCustom} onChange={e => set('modelCustom', e.target.value)} />
        </div>

        <div className="form-field">
          <label className="form-label">Parameters / settings</label>
          <input className="form-input" placeholder="e.g. --ar 16:9 --style raw --chaos 20"
            value={form.params} onChange={e => set('params', e.target.value)} />
        </div>

        {(form.type === 'text' || form.type === 'code' || form.type === 'prompt' || form.type === 'longform') && (
          <div className="form-field">
            <label className="form-label">Output content</label>
            <textarea className="form-textarea"
              placeholder="Paste the output returned by the model..."
              value={form.textOutput} onChange={e => set('textOutput', e.target.value)}
              style={{
                minHeight: form.type === 'longform' ? 160 : 80,
                fontFamily: form.type === 'code' ? 'var(--mono)' : undefined,
                fontSize: form.type === 'code' ? 12 : undefined,
              }} />
          </div>
        )}

        <div className="form-field">
          <label className="form-label">Attach file</label>
          <div onClick={() => fileRef.current.click()} style={{
            border: '0.5px dashed var(--border2)', borderRadius: 'var(--radius-sm)',
            padding: '12px', cursor: 'pointer', textAlign: 'center',
            fontSize: 12.5, color: 'var(--dim)', transition: 'border-color 0.12s',
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border3)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}>
            {file ? (
              <span style={{ color: 'var(--muted)' }}>
                📎 {file.name}
                <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--dim)' }}>
                  ({(file.size / (1024 * 1024)).toFixed(1)} MB)
                </span>
              </span>
            ) : (
              <span>Click to attach image or file</span>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*,video/*,.pdf,.txt,.md"
            style={{ display: 'none' }} onChange={handleFile} />
        </div>

        {previewUrl && file?.type.startsWith('image/') && (
          <div style={{ borderRadius: 'var(--radius-sm)', overflow: 'hidden', border: '0.5px solid var(--border)' }}>
            <img src={previewUrl} alt="preview" style={{ width: '100%', display: 'block', maxHeight: 180, objectFit: 'cover' }} />
          </div>
        )}
        {previewUrl && file?.type.startsWith('video/') && (
          <div style={{ borderRadius: 'var(--radius-sm)', overflow: 'hidden', border: '0.5px solid var(--border)' }}>
            <video src={previewUrl} controls muted style={{ width: '100%', display: 'block', maxHeight: 180 }} />
          </div>
        )}

        {(isPro || isAdmin) && folders && folders.length > 0 && (
          <div className="form-field">
            <label className="form-label">Folder</label>
            <select className="form-select" value={form.folderId} onChange={e => set('folderId', e.target.value)}>
              <option value="">No folder (unfiled)</option>
              {folders.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-field">
          <label className="form-label">Tags</label>
          <input className="form-input" placeholder="portrait, marketing, product (comma separated)"
            value={form.tags} onChange={e => set('tags', e.target.value)} />
        </div>

        <div className="form-field">
          <label className="form-label">Notes</label>
          <textarea className="form-textarea"
            placeholder="What worked, what didn't, variations to try..."
            value={form.notes} onChange={e => set('notes', e.target.value)}
            style={{ minHeight: 70 }} />
        </div>
          <label className="form-label">Source URL <span style={{ fontWeight: 400, color: 'var(--dim)', fontSize: 11 }}>(optional)</span></label>
          <input className="form-input"
            placeholder="https://chat.openai.com or where you used this prompt"
            value={form.sourceUrl} onChange={e => set('sourceUrl', e.target.value)} />

        <div className="form-field">
          <label className="form-label">Visibility</label>
          <div style={{ display: 'flex', gap: 7 }}>
            <div className={`type-opt ${form.isPublic === true ? 'selected' : ''}`}
              style={{ flex: 1, justifyContent: 'center' }}
              onClick={() => set('isPublic', true)}>
              🌐 Public
            </div>
            <div
              className={`type-opt ${form.isPublic === false ? 'selected' : ''}`}
              style={{
                flex: 1, justifyContent: 'center',
                opacity: (!isPro && !isAdmin) ? 0.45 : 1,
                cursor: (!isPro && !isAdmin) ? 'not-allowed' : 'pointer',
              }}
              onClick={() => {
                if (!isPro && !isAdmin) { navigate('/premium'); return; }
                set('isPublic', false);
              }}>
              🔒 Private {(!isPro && !isAdmin) && <span style={{ fontSize: 9, marginLeft: 4, color: 'var(--accent)' }}>PRO</span>}
            </div>
          </div>
          {(!isPro && !isAdmin) && (
            <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 4 }}>
              Private prompts require a Pro plan
            </div>
          )}
        </div>
      </div>

      {/* Upload progress bar */}
      {saving && (
        <div style={{
          padding: '10px 18px 0',
          borderTop: '0.5px solid var(--border)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>
              {uploadPhase === 'compressing' ? '⚙ Compressing image...'
                : uploadPhase === 'uploading' ? '⬆ Uploading file...'
                : uploadPhase === 'saving' ? '💾 Saving entry...'
                : '⏳ Working...'}
            </span>
            {uploadPhase === 'uploading' && (
              <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 500 }}>
                {uploadPct}%
              </span>
            )}
          </div>
          <div style={{
            height: 4, background: 'var(--surface2)',
            borderRadius: 4, overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              borderRadius: 4,
              background: 'var(--accent)',
              transition: uploadPhase === 'uploading' ? 'width 0.2s ease' : 'width 0.4s ease',
              width: uploadPhase === 'compressing' ? '15%'
                : uploadPhase === 'uploading' ? `${Math.max(uploadPct, 5)}%`
                : uploadPhase === 'saving' ? '95%'
                : '0%',
            }} />
          </div>
        </div>
      )}

      <div className="form-footer" style={{
        // On iOS with virtual keyboard open, stay above the keyboard
        // env(keyboard-inset-height) is supported in iOS 15+ Safari PWA
        paddingBottom: 'env(keyboard-inset-height, 0px)',
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        background: 'var(--surface)',
        borderTop: '0.5px solid var(--border)',
      }}>
        <button className="btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
        {dupeEntry && !dupeDismissed && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 8, padding: '10px 12px', marginBottom: 8,
          }}>
            <span style={{ fontSize: 14, flexShrink: 0 }}>⚠</span>
            <div style={{ flex: 1, fontSize: 12, color: 'var(--text)', lineHeight: 1.5 }}>
              <strong style={{ color: '#f59e0b', display: 'block', marginBottom: 2 }}>Possible duplicate</strong>
              You already saved <em>"{dupeEntry.title}"</em> on {new Date(dupeEntry.created_at).toLocaleDateString()}. Save anyway?
            </div>
            <button onClick={() => setDupeDismissed(true)} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--dim)', fontSize: 16, padding: 0, flexShrink: 0,
            }}>×</button>
          </div>
        )}
        <button className="btn-save" onClick={handleSave} disabled={saving}>
          {saving
            ? uploadPhase === 'compressing' ? 'Compressing...'
              : uploadPhase === 'uploading' ? `Uploading ${uploadPct}%`
              : 'Saving...'
            : 'Save entry'}
        </button>
      </div>
    </div>
  );
}
